/**
 * Revenue Goal Widget
 * Phase 9.5b: Dashboard widget showing progress toward annual revenue goal
 *
 * Shows:
 * - YTD Revenue achieved (from dashboard stats)
 * - Progress toward annual goal (% achieved)
 * - Time context (% of year elapsed)
 * - Status indicator: ahead/behind/on-track
 *
 * When goal is 0 (not set), shows a setup prompt with link to settings.
 */
import { useMemo } from 'react';
import { useDashboardStats } from '@/hooks/useDashboardReports';
import { useFinancialSettings } from '@/hooks/useSettings';
import { TrendingUp, TrendingDown, Target, AlertCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Link } from 'react-router-dom';

interface RevenueGoalWidgetProps {
  className?: string;
}

export function RevenueGoalWidget({ className = '' }: RevenueGoalWidgetProps) {
  const { data: statsData, isLoading: statsLoading } = useDashboardStats();
  const { data: financialSettings, isLoading: settingsLoading } = useFinancialSettings();

  // Fiscal year start month (1=Jan, 10=Oct, etc.)
  const fiscalStartMonth = financialSettings?.fiscal_year_start_month ?? 1;

  // Calculate fiscal year elapsed percentage
  const yearProgress = useMemo(() => {
    const now = new Date();
    const monthIndex = fiscalStartMonth - 1; // Date constructor uses 0-based months
    let startOfFiscalYear: Date;
    if (now.getMonth() >= monthIndex) {
      startOfFiscalYear = new Date(now.getFullYear(), monthIndex, 1);
    } else {
      startOfFiscalYear = new Date(now.getFullYear() - 1, monthIndex, 1);
    }
    const endOfFiscalYear = new Date(startOfFiscalYear.getFullYear() + 1, monthIndex, 1);
    const totalDays = (endOfFiscalYear.getTime() - startOfFiscalYear.getTime()) / (1000 * 60 * 60 * 24);
    const daysPassed = (now.getTime() - startOfFiscalYear.getTime()) / (1000 * 60 * 60 * 24);
    return (daysPassed / totalDays) * 100;
  }, [fiscalStartMonth]);

  // Get values from data — use fee revenue (excludes pass-throughs) to match goal target
  const ytdRevenue = statsData?.stats?.ytd_fee_revenue ?? 0;
  const annualGoal = financialSettings?.annual_revenue_goal ?? 0;

  // Calculate achievement percentage
  const achievementPercent = annualGoal > 0 ? (ytdRevenue / annualGoal) * 100 : 0;

  // Determine status: ahead (>5% above pace), on-track (within 5%), behind (<5% below pace)
  const status = useMemo(() => {
    if (annualGoal === 0) return 'none';
    const difference = achievementPercent - yearProgress;
    if (difference > 5) return 'ahead';
    if (difference < -5) return 'behind';
    return 'on-track';
  }, [achievementPercent, yearProgress, annualGoal]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Show setup prompt if goal is 0 (not configured)
  if (!settingsLoading && annualGoal === 0) {
    return (
      <div
        className={`bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow ${className}`}
        data-testid="revenue-goal-widget"
      >
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-3">
            Track your progress toward annual revenue targets
          </p>
          <Link
            to="/settings?tab=financial&section=goals"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <Target size={16} />
            Set Revenue Goal
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (statsLoading || settingsLoading) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 animate-pulse ${className}`}>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  const statusConfig = {
    ahead: {
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      barColor: 'bg-emerald-500',
      label: 'Ahead of pace',
    },
    'on-track': {
      icon: Target,
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
      barColor: 'bg-sky-500',
      label: 'On track',
    },
    behind: {
      icon: TrendingDown,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      barColor: 'bg-amber-500',
      label: 'Behind pace',
    },
    none: {
      icon: AlertCircle,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      barColor: 'bg-gray-300',
      label: 'No goal set',
    },
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <div
      className={`bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow ${className}`}
      data-testid="revenue-goal-widget"
    >
      {/* Edit link */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/settings?tab=financial&section=goals"
          className="text-xs text-gray-400 hover:text-gray-600"
          title="Edit revenue goal in settings"
        >
          Edit goal
        </Link>
        <div className={`${config.bgColor} p-2 rounded-lg`}>
          <StatusIcon className={config.color} size={20} />
        </div>
      </div>

      {/* YTD Revenue */}
      <div className="mb-4">
        <div className={`text-2xl font-bold ${config.color}`}>
          {formatCurrency(ytdRevenue)}
        </div>
        <div className="text-sm text-gray-500">
          of {formatCurrency(annualGoal)} goal ({achievementPercent.toFixed(1)}%)
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="relative">
          <Progress
            value={Math.min(achievementPercent, 100)}
            className="h-3"
          />
          {/* Year progress marker */}
          <div
            className="absolute top-0 h-3 w-0.5 bg-gray-800"
            style={{ left: `${Math.min(yearProgress, 100)}%` }}
            title={`${yearProgress.toFixed(0)}% of year elapsed`}
          />
        </div>

        {/* Legend */}
        <div className="flex justify-between text-xs text-gray-500">
          <span className={config.color}>{config.label}</span>
          <span>Year: {yearProgress.toFixed(0)}% elapsed</span>
        </div>
      </div>
    </div>
  );
}
